/**
 * SidebarContainer Component
 *
 * Unified parent container for the sidebar that can display either:
 * - Chat view (NoteChat)
 * - Audit view (NoteAuditSidebar)
 *
 * Owns the shared header with toggle functionality, eliminating duplicate header logic.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { EditIcon } from '@icons/EditIcon';
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch } from '@store/hooks';

import { setChatVisibility } from '../store/chatSlice';

import { ModeToggle } from './ModeToggle';

export type SidebarMode = 'chat' | 'audit' | 'analysis';

export interface SidebarContainerProps {
  /**
   * Current sidebar mode
   */
  mode: SidebarMode;

  /**
   * Callback when mode changes
   */
  onModeChange: (mode: SidebarMode) => void;

  /**
   * Whether to show the toggle (feature flag controlled)
   */
  showToggle?: boolean;

  /**
   * Number of audit issues to display as badge (computed from RTK Query data)
   */
  auditIssueCount?: number;

  /**
   * Whether there are chat messages (for clear button state)
   */
  hasMessages?: boolean;

  /**
   * Callback when clear chat is requested
   */
  onClearChat?: () => void;

  /**
   * Content to render for chat mode
   */
  chatContent: React.ReactNode;

  /**
   * Content to render for audit mode
   */
  auditContent: React.ReactNode;

  /**
   * Content to render for analysis mode
   */
  analysisContent?: React.ReactNode;

  /**
   * Whether to show the analysis mode option
   */
  showAnalysisMode?: boolean;

  /**
   * Whether to show the chat (Edit) mode option.
   * When false, the Edit tab is hidden (e.g. on completed/signed notes).
   * @default true
   */
  showChatMode?: boolean;

  /**
   * Note ID for analytics tracking
   */
  noteId?: string;

  /**
   * Encounter ID for analytics tracking
   */
  encounterId?: string;
}

/**
 * SidebarContainer - Unified container with shared header
 *
 * Provides:
 * - Close button (left) to hide the sidebar
 * - Toggle (center) to switch between chat and audit views
 * - Action button (right) - clear chat when in chat mode
 */
export const SidebarContainer: React.FC<SidebarContainerProps> = ({
  mode,
  onModeChange,
  showToggle = false,
  auditIssueCount = 0,
  hasMessages = false,
  onClearChat,
  chatContent,
  auditContent,
  analysisContent,
  showAnalysisMode = false,
  showChatMode = true,
  noteId,
  encounterId,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { noteAudit } = useEventTracking();

  // Track when audit sidebar is opened and how long it's open
  const auditOpenedAtRef = useRef<number | null>(null);
  const [dismissedRulesCount, setDismissedRulesCount] = useState(0);

  // Track when switching to audit mode
  useEffect(() => {
    if (mode === 'audit' && auditOpenedAtRef.current === null) {
      auditOpenedAtRef.current = Date.now();
    } else if (mode !== 'audit' && auditOpenedAtRef.current !== null) {
      auditOpenedAtRef.current = null;
    }
  }, [mode]);

  const handleClose = useCallback(() => {
    // Track sidebar closed if in audit mode
    if (mode === 'audit' && auditOpenedAtRef.current !== null) {
      const duration = Date.now() - auditOpenedAtRef.current;
      noteAudit.sidebarClosed({
        note_id: noteId,
        encounter_id: encounterId,
        duration_ms: duration,
        dismissed_rules_count: dismissedRulesCount,
      });
      auditOpenedAtRef.current = null;
    }
    dispatch(setChatVisibility(false));
  }, [dispatch, mode, noteId, encounterId, dismissedRulesCount, noteAudit]);

  const handleToggleChange = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newMode: SidebarMode | null) => {
      if (newMode !== null) {
        // Track when user clicks the Audit tab
        if (newMode === 'audit') {
          noteAudit.tabClicked({
            note_id: noteId,
            encounter_id: encounterId,
            previous_mode: mode as 'chat' | 'audit',
            issue_count: auditIssueCount,
          });
          // Also track sidebarOpened with source
          noteAudit.sidebarOpened({
            note_id: noteId,
            encounter_id: encounterId,
            source: 'tab_toggle',
            issue_count: auditIssueCount,
          });
        }
        onModeChange(newMode);
      }
    },
    [noteAudit, noteId, encounterId, mode, auditIssueCount, onModeChange],
  );

  const handleClearChat = useCallback(() => {
    onClearChat?.();
  }, [onClearChat]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Shared Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          height: 48,
          px: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
          bgcolor: theme.palette.background.paper,
          gap: 1,
        }}
      >
        {/* Close Button */}
        <Tooltip title="Hide sidebar" placement="right">
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                color: theme.palette.text.primary,
                bgcolor: theme.palette.action.hover,
              },
              transition: 'all 0.2s ease',
              flexShrink: 0,
            }}
            aria-label="Hide sidebar"
          >
            <ChevronRightIcon />
          </IconButton>
        </Tooltip>

        {/* Toggle (center) */}
        {showToggle && (
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <ModeToggle
              mode={mode}
              auditIssueCount={auditIssueCount}
              onChange={handleToggleChange}
              minWidth={90}
              size="small"
              showAnalysisMode={showAnalysisMode}
              showChatMode={showChatMode}
            />
          </Box>
        )}

        {/* Action Button - Show clear chat only in chat mode when chat is available */}
        {mode === 'chat' && showChatMode ? (
          <Tooltip title="Start new chat" placement="left">
            <span>
              <IconButton
                onClick={handleClearChat}
                disabled={!hasMessages}
                size="small"
                sx={{
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    color: theme.palette.primary.main,
                    bgcolor: theme.palette.primary.light + '20',
                  },
                  '&:disabled': {
                    color: theme.palette.action.disabled,
                  },
                  transition: 'all 0.2s ease',
                  flexShrink: 0,
                }}
                aria-label="Start new chat"
              >
                <EditIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          // Spacer to maintain layout when no action button
          <Box sx={{ width: 32, flexShrink: 0 }} />
        )}
      </Box>

      {/* Content Area */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {mode === 'chat' && showChatMode ? chatContent : mode === 'audit' ? auditContent : mode === 'analysis' ? analysisContent : auditContent}
      </Box>
    </Box>
  );
};
